import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { Button, Card } from 'antd';

import isEmpty from 'lodash.isempty';

import axios from '../../../axios';

import { capitalize } from '../../../helpers/common';
import { sortNumbers, sortStrings } from '../../../helpers/filter';
import { hasPermission } from '../../../helpers/user';

import { BLANK_FILTER_TEXT } from '../../../constants/livestock';
import messages from '../../../constants/messages';
import { PERMISSIONS } from '../../../constants/permissions';
import { TABLE_CONFIG_COLUMNS, TABLE_CONFIG_KEYS } from '../../../constants/table-configurations';

import { useModal } from '../../../hooks/useModal';

import { fetchLabels } from '../../../redux/actions/label';
import { setSelectedFilters } from '../../../redux/reducers/filters';
import { setSortedData } from '../../../redux/reducers/label';

import AppModal from '../../../components/AppModal';
import AppTable from '../../../components/AppTable';
import { COLUMN_SIZE } from '../../../components/AppTable/constants';
import AppTag from '../../../components/AppTag';
import { errorToastHandler } from '../../../components/action_notifier';

import { useFilterOptions } from './useFilterOptions';

const commonColumnProperties = {
  ellipsis: true,
  sortDirections: ['ascend', 'descend']
};

function Labels(props) {
  const dispatch = useDispatch();

  const { permissions } = useSelector(state => state.user);
  const { selectedFilters } = useSelector(state => state.filters);
  const { sortedData, data: labels, loading } = useSelector(state => state.label);

  const [archiveModalVisible, openArchiveModal, closeArchiveModal] = useModal();

  const [filteredInfo, setFilteredInfo] = useState({});
  const [state, setState] = useState({
    selected_labels: []
  });

  useEffect(() => {
    getLabels();

    return () => {
      dispatch(setSelectedFilters({}));
    };
  }, []);

  useEffect(() => {
    if (labels?.length) {
      dispatch(setSortedData(labels));
    }
  }, [labels]);

  useEffect(() => {
    // on "Clear all filters" set to sortedData that used for calc filter options to original set of data
    if (isEmpty(filteredInfo) && labels?.length) {
      dispatch(setSortedData(labels));
    }
  }, [filteredInfo]);

  function viewCompoundGroup() {
    props.history.push({
      pathname: 'group/new',
      collection: state.selected_labels
        .filter(id => {
          const label = labels?.find(item => item.id === id);

          return label.main_type !== 'collection';
        })
        .map(id => {
          const label = labels?.find(item => item.id === id);

          return {
            label: label.name,
            value: label.id
          };
        }),
      mainType: 'collection'
    });
  }

  async function getLabels(query) {
    try {
      const response = await dispatch(fetchLabels({ query })).unwrap();

      if (response) {
        setState(prevState => ({
          ...prevState,
          selected_labels: []
        }));
      }
    } catch (error) {
      errorToastHandler(error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    }
  }

  function toggleModal(modal) {
    setState(prevState => ({ ...prevState, [modal]: !state[modal] }));
  }

  async function deleteLabels() {
    const removedIds = state.selected_labels.filter(
      selectedId => labels?.find(({ id }) => id === selectedId)?.type === 'custom'
    );

    const response = await axios.delete('labels/bulk', {
      data: { ids: removedIds }
    });

    if (response.status === 200) {
      await getLabels();
      closeArchiveModal();
    }
  }

  const clearFilters = () => {
    setFilteredInfo({});
    dispatch(setSelectedFilters({}));
  };

  const handleTableChange = useCallback(
    (pagination, filters, sorter, { currentDataSource }) => {
      setFilteredInfo(filters);

      dispatch(setSelectedFilters(filters));
      dispatch(setSortedData(currentDataSource));
    },
    [setFilteredInfo, dispatch]
  );

  const selectRecords = selected => {
    setState(prevState => ({
      ...prevState,
      selected_labels: selected
    }));
  };

  const actions = useMemo(() => {
    const availableActions = [];

    if (hasPermission([PERMISSIONS.LABELS.CREATE], permissions)) {
      availableActions.push({
        label: (
          <Link className="app-link" to="group/new">
            Add new group
          </Link>
        )
      });
    }

    if (hasPermission([PERMISSIONS.LABELS.DELETE_OWN], permissions)) {
      availableActions.push({
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={!state.selected_labels?.length}
            onClick={openArchiveModal}>
            Archive group
          </Button>
        )
      });
    }

    if (hasPermission([PERMISSIONS.LABELS.VIEW_OWN], permissions)) {
      availableActions.push({
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={!state.selected_labels?.length}
            onClick={viewCompoundGroup}>
            Create group collection
          </Button>
        )
      });
    }

    availableActions.push({
      label: (
        <Button
          color="default"
          size="small"
          variant="link"
          disabled={!Object.values(selectedFilters)?.length}
          onClick={clearFilters}>
          Clear all filters
        </Button>
      )
    });

    return availableActions;
  }, [selectedFilters, state.selected_labels, permissions]);

  const columnFilterOptions = useFilterOptions(sortedData);

  const columns = [
    {
      ...commonColumnProperties,
      title: 'Group ID',
      dataIndex: 'identifier',
      configColumnKey: TABLE_CONFIG_COLUMNS.ID,
      filteredValue: filteredInfo.identifier || null,
      fixed: 'left',
      searchable: true,
      width: COLUMN_SIZE.XS,
      render: (value, record) => <Link to={`group/${record.id}`}>{record.identifier}</Link>,
      sorter: (a, b) => sortStrings(a.identifier, b.identifier)
    },
    {
      ...commonColumnProperties,
      title: 'Group Name',
      dataIndex: 'name',
      configColumnKey: TABLE_CONFIG_COLUMNS.NAME,
      filteredValue: filteredInfo.name || null,
      searchable: true,
      width: COLUMN_SIZE.XL,
      render: (value, record) => (
        <Link to={`group/${record.id}`}>
          <AppTag style={{ background: record.colour, color: 'white' }}>{record.name}</AppTag>
        </Link>
      ),
      sorter: (a, b) => sortStrings(a.name, b.name)
    },
    {
      ...commonColumnProperties,
      title: 'Group Type',
      dataIndex: 'main_type',
      configColumnKey: TABLE_CONFIG_COLUMNS.TYPE,
      filteredValue: filteredInfo.main_type || null,
      filters: columnFilterOptions.types,
      width: COLUMN_SIZE.XL,
      onFilter: (value, record) => (value === BLANK_FILTER_TEXT ? !record?.main_type : record?.main_type === value),
      render: value => capitalize(value),
      sorter: (a, b) => sortStrings(a.main_type, b.main_type)
    },
    {
      ...commonColumnProperties,
      title: 'Farm',
      dataIndex: 'farm',
      configColumnKey: TABLE_CONFIG_COLUMNS.FARM,
      filteredValue: filteredInfo.farm || null,
      filters: columnFilterOptions.farms,
      width: COLUMN_SIZE.XL,
      onFilter: (value, record) => (value === BLANK_FILTER_TEXT ? !record?.farm : record?.farm?.name === value),
      render: (value, record) => <Link to={`farms/${record.farm?.id}`}>{record?.farm?.name}</Link>,
      sorter: (a, b) => sortStrings(a.farm?.name, b.farm?.name)
    },
    {
      ...commonColumnProperties,
      title: '# of Livestock in Group',
      dataIndex: 'animals_count',
      configColumnKey: TABLE_CONFIG_COLUMNS.NUM_OF_LIVESTOCK,
      filteredValue: filteredInfo.animals_count || null,
      searchable: true,
      width: COLUMN_SIZE.MD,
      onFilter: (value, record) => record.animals_count === +value,
      sorter: (a, b) => sortNumbers(a.animals_count, b.animals_count)
    }
  ];

  return (
    <>
      <Card>
        <AppTable
          description="List of Groups"
          headerClass="py-2"
          searchPlaceholder="Search Groups"
          title="Groups"
          actions={actions}
          baseColumns={columns}
          dataSource={labels}
          filterable={false}
          loading={loading}
          tableConfigKey={TABLE_CONFIG_KEYS.GROUPS}
          handleOnChange={handleTableChange}
          handleOnSelectRecords={selectRecords}
          handleSearchChange={getLabels}
        />
      </Card>

      <AppModal
        confirmButtonColor="danger"
        confirmButtonText="Archive"
        title="Archive groups"
        isOpen={archiveModalVisible}
        handleCancel={closeArchiveModal}
        handleConfirm={deleteLabels}>
        <div className="py-4">{messages.ARCHIVE_CONFIRMATION('groups')}</div>
      </AppModal>
    </>
  );
}

export default memo(Labels);
